<template>
  <div class="modal fade show" style="display: block; background: #1c1d1c33;">
    <div
      class="modal-dialog modal-dialog-centered modal-xl draggable-dialog"
      ref="dialogRef"
      :style="dialogStyle"
    >
      <div class="modal-content">
        <div
          class="d-flex justify-content-between px-2 my-1 align-item-center draggable-handle"
          @mousedown="onMouseDown"
        >
          <h4 class="mb-0">Review Voucher</h4>
          <button @click="onClickClose" type="button" class="btn-close" aria-label="Close"></button>
        </div>
        <hr class="mt-0 mb-0">

        <div v-if="loadingDetail" class="text-center py-3">Loading...</div>

        <div v-if="! loadingDetail && detail" class="p-2">
          <div class="row mb-2">
            <div class="col-md-4">
              <label class="text-muted">Voucher No</label>
              <div class="fw-bold">{{ detail.voucher_no }}</div>
            </div>
            <div class="col-md-4">
              <label class="text-muted">Date</label>
              <div class="fw-bold">{{ detail.date }}</div>
            </div>
            <div class="col-md-4">
              <label class="text-muted">Party</label>
              <div class="fw-bold">{{ detail.party_name }}</div>
            </div>
            <div class="col-md-4 mt-1">
              <label class="text-muted">Voucher Type</label>
              <div class="fw-bold">{{ detail.voucher_type }}</div>
            </div>
            <div class="col-md-4 mt-1">
              <label class="text-muted">Status</label>
              <div><span class="fw-bold">{{ detail.status }}</span></div>
            </div>
            <div class="col-md-4 mt-1">
              <label class="text-muted">Amount</label>
              <div class="fw-bold">{{ commaFormat(detail.total_amount) }}</div>
            </div>
            <div v-if="detail.note" class="col-12 mt-1">
              <label class="text-muted">Note</label>
              <div>{{ detail.note }}</div>
            </div>
          </div>

          <h5 class="mt-2">Line Items</h5>
          <table class="table table-bordered table-sm">
            <thead>
              <tr>
                <th>Account Head</th>
                <th>Note</th>
                <th class="text-end">Debit</th>
                <th class="text-end">Credit</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="! detail.ledgers || ! detail.ledgers.length">
                <td colspan="4" class="text-center">No line items.</td>
              </tr>
              <tr v-for="line in detail.ledgers" :key="line.id">
                <td>{{ line.account_head }}</td>
                <td>{{ line.note }}</td>
                <td class="text-end">{{ commaFormat(line.debit) }}</td>
                <td class="text-end">{{ commaFormat(line.credit) }}</td>
              </tr>
            </tbody>
          </table>

          <h5 class="mt-2">Cheque</h5>
          <div class="row">
            <div class="col-md-6">
              <label class="form-label">Bank Account</label>
              <v-select
                placeholder="Select Bank Account"
                v-model="selectedAccountHeadId"
                :options="cashAndBank"
                label="name"
                :reduce="item => item.id"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label">Cheque</label>
              <AsyncSelect
                placeholder="Select Cheque"
                v-model="selectedCheque"
                :api-service="fetchChequeRegisterData"
                :additional-query="{ account_head_id: selectedAccountHeadId, status: 'active' }"
                :disabled="! selectedAccountHeadId"
                label="cheque_number"
                :format-label="option => option.cheque_number"
                :reduce="item => item"
              />
            </div>
            <div class="col-12 mt-1">
              <button
                class="btn btn-outline-primary btn-sm"
                :disabled="! selectedCheque || ! selectedAccountHeadId || chequeSaving"
                @click="saveCheque"
              >
                {{ chequeSaving ? 'Saving...' : 'Update Cheque' }}
              </button>
            </div>
          </div>

          <h5 class="mt-2">Approval Log</h5>
          <div v-if="! detail.workflow_approval_logs.length" class="text-muted">No approval activity yet.</div>
          <div v-for="log in detail.workflow_approval_logs" :key="log.id" class="mb-1">
            <LogList :approval-log="formatLog(log)" />
          </div>

          <hr>

          <h5>Take Action</h5>
          <div class="row">
            <div class="col-12">
              <label class="form-label">
                Comment <span v-if="commentRequired" class="required">*</span>
              </label>
              <textarea
                v-model="comment"
                class="form-control"
                rows="2"
                placeholder="Write your comment"
              ></textarea>
            </div>
          </div>
          <div class="text-end mt-2">
            <button
              class="btn btn-danger me-1"
              :disabled="submitting || ! comment"
              @click="submitStatus('rejected')"
            >Reject</button>
            <button
              class="btn btn-warning me-1"
              :disabled="submitting || ! comment"
              @click="submitStatus('returned')"
            >Return</button>
            <button
              class="btn btn-success"
              :disabled="submitting"
              @click="submitStatus((detail && detail.status !== 'pending_approve') ? 'pending_approve' : 'approved')"
            >{{ getConfirmButtonName() }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, inject } from 'vue'
import { useRoute } from 'vue-router'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import LogList from '@/components/molecule/approval-matrix/approval-log/LogList.vue'
import handleAccounting from '@/services/modules/accounting'
import handlePayment from '@/services/modules/payment'
import handlePaymentRequisition from '@/services/modules/paymentRequisition'
import figureFormatter from '@/services/utils/figureFormatter'

const props = defineProps({
  ledgerMasterId: { type: Number, required: true },
  workflowMasterId: { type: Number, default: null }
})
const emit = defineEmits(['close', 'updated'])

const showError = inject('showError')
const showSuccess = inject('showSuccess')
const $route = useRoute()

const { fetchChequeRegisterData, fetchSingleChequeRegister } = handleAccounting()
const { fetchCashAndBank } = handlePayment()
const { fetchPaymentRequisitionReviewDetails, updateLedgerCheque, updateLedgerStatus } = handlePaymentRequisition()
const { commaFormat } = figureFormatter()

const loadingDetail = ref(false)
const submitting = ref(false)
const chequeSaving = ref(false)
const detail = ref(null)
const cashAndBank = ref([])
const selectedAccountHeadId = ref(null)
const selectedCheque = ref(null)
const comment = ref('')

const commentRequired = computed(() => true)

const companyId = computed(() => $route.params.companyId)
const pageId = computed(() => $route.params.pageId)

const loadDetail = async () => {
  loadingDetail.value = true
  try {
    const res = await fetchPaymentRequisitionReviewDetails(props.ledgerMasterId, `?company_id=${companyId.value}`)
    if (! res.status) {
      showError(res.message)
      return
    }
    detail.value = res.data
    if (detail.value.cheque_account_head_id) {
      selectedAccountHeadId.value = detail.value.cheque_account_head_id
    }
    if (detail.value.cheque_register_id) {
      const chequeRes = await fetchSingleChequeRegister(
        detail.value.cheque_register_id,
        `?company_id=${companyId.value}`
      ).catch(() => null)
      if (chequeRes && chequeRes.status) selectedCheque.value = chequeRes.data
    }
  } catch (err) {
    showError('Failed to load voucher detail.')
  } finally {
    loadingDetail.value = false
  }
}

watch(selectedAccountHeadId, (newVal, oldVal) => {
  if (oldVal === null) return
  if (! detail.value || newVal === detail.value.cheque_account_head_id) return
  selectedCheque.value = null
})

const loadCashAndBank = async () => {
  const res = await fetchCashAndBank(companyId.value).catch(() => null)
  if (res && res.data) cashAndBank.value = res.data
}

const formatLog = (log) => ({
  user: {
    name: log.user_name || '',
    email: log.user_email || ''
  },
  logged_at: log.created_at,
  status: log.status,
  comment: log.comment || ''
})

const getConfirmButtonName = () => {
  if (detail.value && detail.value.status === 'pending_approve') return 'Approve'
  return 'Confirm'
}

const saveCheque = async () => {
  if (! selectedCheque.value || ! selectedAccountHeadId.value) return
  chequeSaving.value = true
  try {
    const res = await updateLedgerCheque(props.ledgerMasterId, {
      company_id: companyId.value,
      cheque_register_id: selectedCheque.value.id,
    })
    if (! res.status) {
      showError(res.message)
      return
    }
    showSuccess(res.message)
    loadDetail()
  } catch (err) {
    showError('Failed to update cheque.')
  } finally {
    chequeSaving.value = false
  }
}

const submitStatus = async (status) => {
  if (! comment.value && status !== 'approved') {
    showError('Comment is required.')
    return
  }
  if (! props.workflowMasterId) {
    showError('No active workflow is configured for this page.')
    return
  }
  submitting.value = true
  try {
    const res = await updateLedgerStatus(props.ledgerMasterId, {
      company_id: companyId.value,
      workflow_master_id: props.workflowMasterId,
      status,
      comment: comment.value,
    })

    if (! res.status) {
      showError(res.message)
      return
    }
    showSuccess(res.message)
    emit('updated')
    emit('close')
  } catch (err) {
    console.log(err)
  } finally {
    submitting.value = false
  }
}

const onClickClose = () => emit('close')

const dialogRef = ref(null)
const offset = ref({ x: 0, y: 0 })
const dragState = ref({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 })

const dialogStyle = computed(() => ({
  transform: `translate(${offset.value.x}px, ${offset.value.y}px)`
}))

const onMouseMove = (e) => {
  if (! dragState.value.active) return
  offset.value = {
    x: dragState.value.originX + (e.clientX - dragState.value.startX),
    y: dragState.value.originY + (e.clientY - dragState.value.startY),
  }
}

const onMouseUp = () => {
  dragState.value.active = false
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
}

const onMouseDown = (e) => {
  if (e.target && e.target.closest('.btn-close')) return
  dragState.value = {
    active: true,
    startX: e.clientX,
    startY: e.clientY,
    originX: offset.value.x,
    originY: offset.value.y,
  }
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}

onMounted(() => {
  loadCashAndBank()
  loadDetail()
})

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
})
</script>

<style scoped>
.modal-xl {
  max-width: 1100px;
}
.draggable-dialog {
  user-select: none;
}
.draggable-handle {
  cursor: move;
}
</style>
